import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Calendar, Clock, MapPin, Radio, PlayCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useLeadDialog } from '../context/LeadDialogContext';

const UPCOMING = [
  {
    id: 'u1',
    type: 'Webinar',
    title: 'Operationalizing AI Agents for SRE',
    date: 'Mar 06, 2026',
    time: '5:00 PM IST · 60 min',
    where: 'Online · Live',
    speakers: ['Aarav Mehta · Principal Engineer', 'Priya Natarajan · Customer Success'],
    cover: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=1200&q=70',
  },
  {
    id: 'u2',
    type: 'Event',
    title: 'MetaxuOne Connect · Mumbai',
    date: 'Mar 22, 2026',
    time: '9:00 AM IST · Full day',
    where: 'JW Marriott, Sahar · Mumbai',
    speakers: ['MetaxuOne Leadership', 'Partner ecosystem'],
    cover: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=70',
  },
  {
    id: 'u3',
    type: 'Workshop',
    title: 'Building the Context Model — hands-on lab',
    date: 'Apr 10, 2026',
    time: '4:00 PM IST · 90 min',
    where: 'Online · Limited seats',
    speakers: ['Sara Iyer · Product Lead', 'Devansh Kapoor · Platform Architect'],
    cover: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&q=70',
  },
];

const ON_DEMAND = [
  {
    id: 'o1', type: 'Webinar', title: 'Self-healing operations: a CIO playbook',
    duration: '42 min', date: 'Jan 2026',
    cover: 'https://images.unsplash.com/photo-1517021897933-0e0319cfbc28?w=900&q=70',
  },
  {
    id: 'o2', type: 'Webinar', title: 'Migrating from siloed monitoring to a context graph',
    duration: '38 min', date: 'Dec 2025',
    cover: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=900&q=70',
  },
  {
    id: 'o3', type: 'Panel', title: 'Industry leaders on AI in BFSI operations',
    duration: '55 min', date: 'Nov 2025',
    cover: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=900&q=70',
  },
  {
    id: 'o4', type: 'Talk', title: 'Designing guardrails for autonomous remediation',
    duration: '29 min', date: 'Nov 2025',
    cover: 'https://images.unsplash.com/photo-1559523161-0fc0d8b38a7a?w=900&q=70',
  },
  {
    id: 'o5', type: 'Workshop', title: 'Wiring telemetry into the agent runtime',
    duration: '62 min', date: 'Oct 2025',
    cover: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=900&q=70',
  },
  {
    id: 'o6', type: 'Webinar', title: 'Capacity planning with operational intelligence',
    duration: '47 min', date: 'Sep 2025',
    cover: 'https://images.unsplash.com/photo-1574169208507-84376144848b?w=900&q=70',
  },
];

const TABS = ['Upcoming', 'On-demand'];

const Events = () => {
  const { open: openLead } = useLeadDialog();
  const [tab, setTab] = useState('Upcoming');
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-black min-h-screen" data-testid="events-page">
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(60% 50% at 50% 0%, rgba(59,130,246,0.22), transparent), radial-gradient(50% 60% at 90% 30%, rgba(255,255,255,0.05), transparent)',
          }} />
          <div aria-hidden className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at top, black 30%, transparent 75%)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-16">
            <nav className="flex items-center gap-1.5 text-[12.5px] text-neutral-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-400">Resources</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-300">Webinars &amp; Events</span>
            </nav>
            <div className="mt-7 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30">
                <Radio className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[11.5px] uppercase tracking-[0.15em] text-blue-300">Live &amp; on-demand</span>
              </div>
              <h1 className="mt-4 text-[34px] sm:text-[48px] md:text-[60px] lg:text-[72px] leading-[1.02] sm:leading-[0.98] tracking-[-0.03em] font-semibold text-white">
                Webinars &amp; <span className="italic font-light brand-gradient-text">Events.</span>
              </h1>
              <p className="mt-5 text-[16.5px] lg:text-[18px] text-neutral-300 leading-relaxed">
                Join MetaxuOne engineers, product leaders and customers — live online, in-person, or on-demand. From technical deep-dives to executive briefings.
              </p>
            </div>

            {/* Tabs */}
            <div className="mt-10 inline-flex p-1 rounded-full border border-neutral-800 bg-neutral-950">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  data-testid={`events-tab-${t.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`text-[13px] px-4 py-2 rounded-full transition-colors ${
                    tab === t ? 'bg-white text-neutral-950' : 'text-neutral-300 hover:text-white'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* UPCOMING */}
        {tab === 'Upcoming' && (
          <section className="relative bg-neutral-950 border-b border-neutral-900">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
              <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">What&apos;s coming up</p>
              <div className="mt-8 grid lg:grid-cols-3 gap-5">
                {UPCOMING.map((e) => (
                  <article key={e.id} className="group rounded-2xl border border-neutral-800 bg-black overflow-hidden flex flex-col hover:border-neutral-600 transition-colors">
                    <div className="relative h-44 overflow-hidden">
                      <img src={e.cover} alt={e.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                      <div className="absolute top-3 left-3 text-[10.5px] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-black/60 border border-white/10 text-white">
                        {e.type}
                      </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="text-[18px] font-semibold text-white leading-snug">{e.title}</h3>
                      <div className="mt-4 space-y-2 text-[13px] text-neutral-300">
                        <div className="inline-flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-blue-300" /> {e.date}</div>
                        <div className="inline-flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-blue-300" /> {e.time}</div>
                        <div className="inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-blue-300" /> {e.where}</div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-neutral-900">
                        <p className="text-[11px] uppercase tracking-[0.16em] text-neutral-500">Speakers</p>
                        <ul className="mt-2 text-[12.5px] text-neutral-300 space-y-1">
                          {e.speakers.map((s) => <li key={s}>{s}</li>)}
                        </ul>
                      </div>
                      <Button
                        onClick={() => openLead({ source: `event-${e.id}`, title: `Register · ${e.title}` })}
                        className="mt-6 h-11 px-5 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[13.5px] font-medium inline-flex items-center gap-2 self-start"
                        data-testid={`event-register-${e.id}`}
                      >
                        Register <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ON DEMAND */}
        {tab === 'On-demand' && (
          <section className="relative bg-neutral-950 border-b border-neutral-900">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
              <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Watch anytime</p>
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {ON_DEMAND.map((e) => (
                  <article key={e.id} className="group rounded-2xl border border-neutral-800 bg-black overflow-hidden hover:border-neutral-600 transition-colors">
                    <div className="relative h-40 overflow-hidden">
                      <img src={e.cover} alt={e.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/15 transition-colors">
                        <PlayCircle className="w-12 h-12 text-white drop-shadow-lg" />
                      </div>
                      <div className="absolute top-3 left-3 text-[10.5px] uppercase tracking-[0.16em] px-2 py-1 rounded-full bg-black/60 border border-white/10 text-white">
                        {e.type}
                      </div>
                      <div className="absolute bottom-3 right-3 text-[11px] px-2 py-1 rounded bg-black/70 text-white">{e.duration}</div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-[16px] font-semibold text-white leading-snug">{e.title}</h3>
                      <div className="mt-3 flex items-center justify-between text-[12px] text-neutral-500">
                        <span>{e.date}</span>
                        <button
                          onClick={() => openLead({ source: `ondemand-${e.id}`, title: `Watch · ${e.title}` })}
                          className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200"
                          data-testid={`ondemand-watch-${e.id}`}
                        >
                          Watch now <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="bg-black">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <h3 className="text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
                  Host a private briefing <span className="italic font-light brand-gradient-text">for your team.</span>
                </h3>
                <p className="mt-4 text-[15.5px] text-neutral-300 max-w-xl">
                  We&apos;ll bring an architect and a customer leader to share what&apos;s working in operations modernization — tailored to your industry and stack.
                </p>
              </div>
              <div className="lg:col-span-5 flex lg:justify-end">
                <Button
                  onClick={() => openLead({ source: 'events-private-cta', title: 'Request a private briefing' })}
                  className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                  data-testid="events-private-cta-btn"
                >
                  Request a briefing <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Events;
